// Comprehensive form field validation for all steps
// This file contains validation functions for the entire AutoRegisterForm

/**
 * Validates text fields (name, company name, address)
 * @param {string} value - The value to validate
 * @param {string} fieldName - The name of the field for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validateTextField = (value, fieldName) => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      error: `${fieldName} jest wymagane`
    };
  }
  
  if (value.trim().length < 2) {
    return {
      isValid: false,
      error: `${fieldName} musi mieć przynajmniej 2 znaki`
    };
  }
  
  if (value.trim().length > 100) {
    return {
      isValid: false,
      error: `${fieldName} nie może mieć więcej niż 100 znaków`
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates phone number
 * @param {string} value - The phone number to validate
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validatePhoneNumber = (value) => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      error: 'Numer telefonu jest wymagany'
    };
  }
  
  // Remove all non-digit characters to count only digits
  const digitsOnly = value.replace(/[^0-9]/g, '');
  
  if (digitsOnly.length < 8) {
    return {
      isValid: false,
      error: 'Numer telefonu musi mieć przynajmniej 8 cyfr'
    };
  }
  
  if (digitsOnly.length > 12) {
    return {
      isValid: false,
      error: 'Numer telefonu nie może mieć więcej niż 12 cyfr'
    };
  }
  
  // Check if there are two consecutive spaces
  if (value.includes('  ')) {
    return {
      isValid: false,
      error: 'Numer telefonu nie może mieć dwóch spacji pod rząd'
    };
  }
  
  // Check if + is only at the beginning
  const plusCount = (value.match(/\+/g) || []).length;
  if (plusCount > 1 || (plusCount === 1 && !value.startsWith('+'))) {
    return {
      isValid: false,
      error: 'Symbol + może być tylko na początku numeru'
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates email address
 * @param {string} value - The email to validate
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validateEmail = (value) => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      error: 'Adres email jest wymagany'
    };
  }
  
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(value.trim())) {
    return {
      isValid: false,
      error: 'Wprowadź poprawny adres email'
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates NIP field (optional field)
 * @param {string} value - The NIP value to validate
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validateNIP = (value) => {
  // NIP is optional, so if empty it's valid
  if (!value || value.trim().length === 0) {
    return {
      isValid: true,
      error: null
    };
  }
  
  // Remove all non-digit characters to count only digits
  const digitsOnly = value.replace(/[^0-9]/g, '');
  
  if (digitsOnly.length !== 10) {
    return {
      isValid: false,
      error: 'NIP musi mieć dokładnie 10 cyfr'
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates selection fields (company type, time tracking method, etc.)
 * @param {string} value - The selected value
 * @param {string} fieldName - The name of the field for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validateSelection = (value, fieldName) => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      error: `Musisz wybrać ${fieldName}`
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates terms acceptance
 * @param {boolean} accepted - Whether terms are accepted
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export const validateTerms = (accepted) => {
  if (!accepted) {
    return {
      isValid: false,
      error: 'Musisz zaakceptować regulamin i politykę prywatności'
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates all fields for a specific step
 * @param {Object} formData - The form data object
 * @param {number} step - The current step number
 * @returns {Object} - Validation result with isValid boolean and errors object
 */
export const validateStep = (formData, step) => {
  const errors = {};
  let isValid = true;
  
  switch (step) {
    case 1:
      // Validate StepOne fields
      const nameValidation = validateTextField(formData.name, 'Imię');
      const emailValidation = validateEmail(formData.email);
      
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
        isValid = false;
      }
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error;
        isValid = false;
      }
      break;
      
    case 2:
      // Validate StepTwo fields
      const companyNameValidation = validateTextField(formData.companyName, 'Nazwa firmy');
      const companyAddressValidation = validateTextField(formData.companyAddress, 'Adres firmy');
      const nipValidation = validateNIP(formData.nip);
      const termsValidation = validateTerms(formData.acceptedTerms);
      
      if (!companyNameValidation.isValid) {
        errors.companyName = companyNameValidation.error;
        isValid = false;
      }
      if (!companyAddressValidation.isValid) {
        errors.companyAddress = companyAddressValidation.error;
        isValid = false;
      }
      if (!nipValidation.isValid) {
        errors.nip = nipValidation.error;
        isValid = false;
      }
      if (!termsValidation.isValid) {
        errors.acceptedTerms = termsValidation.error;
        isValid = false;
      }
      break;
      
    default:
      break;
  }
  
  return {
    isValid,
    errors
  };
};

/**
 * Validates the entire form for final submission
 * @param {Object} formData - The complete form data
 * @returns {Object} - Validation result with isValid boolean and errors object
 */
export const validateCompleteForm = (formData) => {
  const errors = {};
  let isValid = true;
  
  // Validate all required fields across all steps
  const step1Validation = validateStep(formData, 1);
  const step2Validation = validateStep(formData, 2);
  const step3Validation = validateStep(formData, 3);
  const step4Validation = validateStep(formData, 4);
  const step5Validation = validateStep(formData, 5);
  
  // Merge all errors
  Object.assign(errors, step1Validation.errors);
  Object.assign(errors, step2Validation.errors);
  Object.assign(errors, step3Validation.errors);
  Object.assign(errors, step4Validation.errors);
  Object.assign(errors, step5Validation.errors);
  
  // Check if GPS was selected and validate step 6
  if (formData.timeTrackingMethod === 'gps') {
    const step6Validation = validateStep(formData, 6);
    Object.assign(errors, step6Validation.errors);
  }
  
  // Overall validation
  isValid = step1Validation.isValid && 
            step2Validation.isValid && 
            step3Validation.isValid && 
            step4Validation.isValid && 
            step5Validation.isValid &&
            (formData.timeTrackingMethod !== 'gps' || validateStep(formData, 6).isValid);
  
  return {
    isValid,
    errors
  };
};

/**
 * Gets validation rules for a specific field
 * @param {string} fieldName - The name of the field
 * @returns {Object} - Validation rules object
 */
export const getFieldValidationRules = (fieldName) => {
  const rules = {
    name: {
      minLength: 2,
      maxLength: 100,
      required: true,
      type: 'text'
    },
    email: {
      required: true,
      type: 'email',
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    },
    phone: {
      minDigits: 8,
      maxDigits: 12,
      required: true,
      type: 'phone',
      allowPlus: true,
      allowSpaces: true
    },
    companyName: {
      minLength: 2,
      maxLength: 100,
      required: true,
      type: 'text'
    },
    companyAddress: {
      minLength: 2,
      maxLength: 100,
      required: true,
      type: 'text'
    },
    nip: {
      required: false,
      type: 'nip',
      exactDigits: 10
    },
    companyType: {
      required: true,
      type: 'selection'
    },
    timeTrackingMethod: {
      required: true,
      type: 'selection'
    },
    gpsMode: {
      required: false,
      type: 'selection',
      dependsOn: 'timeTrackingMethod',
      dependsOnValue: 'gps'
    },
    registrationMethod: {
      required: false,
      type: 'selection',
      dependsOn: 'timeTrackingMethod',
      dependsOnValue: 'manual'
    },
    scheduleUsage: {
      required: false,
      type: 'selection',
      dependsOn: 'timeTrackingMethod',
      dependsOnValue: 'gps'
    }
  };
  
  return rules[fieldName] || null;
};
